/**
 * Utility functions to handle Google Drive URLs for better embedding
 */

/**
 * Extract file ID from various Google Drive URL formats
 */
export const extractGoogleDriveFileId = (url: string): string | null => {
  if (!url) return null;

  // Pattern 1: https://drive.google.com/uc?export=view&id=FILE_ID
  const ucPattern = /[?&]id=([^&]+)/;
  const ucMatch = ucPattern.exec(url);
  if (ucMatch) return ucMatch[1];

  // Pattern 2: https://drive.google.com/file/d/FILE_ID/view
  const filePattern = /\/file\/d\/([^/]+)/;
  const fileMatch = filePattern.exec(url);
  if (fileMatch) return fileMatch[1];

  // Pattern 3: https://drive.google.com/open?id=FILE_ID
  const openPattern = /open\?id=([^&]+)/;
  const openMatch = openPattern.exec(url);
  if (openMatch) return openMatch[1];

  // Pattern 4: https://lh3.googleusercontent.com/d/FILE_ID
  const googleUserContentPattern = /\/d\/([^/?]+)/;
  const googleUserContentMatch = googleUserContentPattern.exec(url);
  if (googleUserContentMatch && url.includes('googleusercontent.com')) return googleUserContentMatch[1];

  return null;
};

/**
 * Convert Google Drive URL to embeddable thumbnail URL
 * This format works better for embedding images in img tags
 */
export const getGoogleDriveThumbnailUrl = (
  url: string,
  size = 1000
): string => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return url;

  // Use Google Drive thumbnail API which is more reliable for embedding
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
};

/**
 * Convert Google Drive URL to direct download URL
 */
export const getGoogleDriveDirectUrl = (url: string): string => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return url;

  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};

/**
 * Convert Google Drive URL to GoogleUserContent URL (better for embedding)
 */
export const getGoogleUserContentUrl = (url: string): string => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return url;

  return `https://lh3.googleusercontent.com/d/${fileId}`;
};

/**
 * Check if URL is a Google Drive URL
 */
export const isGoogleDriveUrl = (url: string): boolean => {
  if (!url) return false;
  return (
    url.includes("drive.google.com") ||
    url.includes("docs.google.com") ||
    (url.includes("googleusercontent.com") && url.includes("/d/"))
  );
};

/**
 * Convert any image URL to a properly formatted URL for display
 * Handles Google Drive URLs and returns them in an embeddable format
 */
export const getImageUrl = (url: unknown, size = 2000): string => {
  if (!url) return "";

  // API values are not always plain strings (e.g. multi-file attachments arrive
  // as an array of URLs). Coerce defensively so `.trim()` can never throw:
  // an array collapses to its first usable entry, anything else to a string.
  const rawUrl = Array.isArray(url)
    ? url.find((u) => typeof u === "string" && u.trim()) ?? ""
    : typeof url === "string"
      ? url
      : String(url);

  if (!rawUrl) return "";

  // Normalize dynamic URL inputs from API (quotes, spaces, protocol-less, relative)
  const normalizedUrl = rawUrl
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/^'+|'+$/g, "");

  if (!normalizedUrl) return normalizedUrl;

  if (normalizedUrl.startsWith("//")) {
    return `https:${normalizedUrl}`;
  }

  if (/^(https?:|data:|blob:)/i.test(normalizedUrl)) {
    // keep absolute/data/blob URL as-is and apply Google-specific conversion below if needed
  } else if (normalizedUrl.startsWith("/")) {
    return `${window.location.origin}${normalizedUrl}`;
  } else if (normalizedUrl.startsWith("api/")) {
    return `${window.location.origin}/${normalizedUrl}`;
  } else if (/^[\w.-]+\.[a-z]{2,}([/:?#]|$)/i.test(normalizedUrl)) {
    return `https://${normalizedUrl}`;
  } else {
    // Unknown relative path-like value; return normalized value as fallback
    return normalizedUrl;
  }

  // Direct googleusercontent links are already image-renderable; keep as-is.
  if (normalizedUrl.includes("googleusercontent.com") && normalizedUrl.includes("/d/")) {
    return normalizedUrl;
  }

  // If it's a Google Drive URL, convert it
  if (isGoogleDriveUrl(normalizedUrl)) {
    const fileId = extractGoogleDriveFileId(normalizedUrl);
    if (fileId) {
      // Use thumbnail API for all Google Drive formats; it is the most reliable for <img>
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
    }
  }

  // For all other URLs (S3, regular URLs, etc.), return as-is
  return normalizedUrl;
};
